import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { BusinessInfo, Supplier, SupplierInvoice } from '../types';
import { computePoTotals, gstModeLabel } from './nzGst';

export interface SupplierInvoicePdfInput {
  invoice: SupplierInvoice;
  supplier: Supplier;
  businessInfo: BusinessInfo;
}

function formatDate(ms?: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function generateSupplierInvoicePdf(
  input: SupplierInvoicePdfInput,
  filename?: string,
): void {
  const { invoice, supplier, businessInfo } = input;
  const doc = new jsPDF({ orientation: 'portrait' });
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const gstMode = invoice.gstMode ?? 'inclusive';
  const poItems = invoice.items.map((line) => ({
    lineId: line.lineId,
    productName: line.description,
    quantity: line.quantity,
    costPerUnit: line.unitPrice,
    unit: line.unit as 'units',
  }));
  const totals = computePoTotals(poItems, gstMode);

  let y = margin;
  doc.setFontSize(20);
  doc.setFont(undefined as any, 'bold');
  doc.text('Tax Invoice', margin, y);
  doc.setFont(undefined as any, 'normal');
  y += 10;

  doc.setFontSize(10);
  doc.setFont(undefined as any, 'bold');
  doc.text('From (supplier)', margin, y);
  doc.setFont(undefined as any, 'normal');
  y += 5;
  doc.text(supplier.name, margin, y);
  y += 5;
  if (supplier.address) {
    doc.text(supplier.address, margin, y);
    y += 5;
  }
  if (supplier.gstNumber) {
    doc.text(`GST #${supplier.gstNumber}`, margin, y);
    y += 5;
  }
  if (supplier.nzbn) {
    doc.text(`NZBN ${supplier.nzbn}`, margin, y);
    y += 5;
  }
  y += 4;

  const rightCol = pageWidth / 2 + 10;
  doc.setFont(undefined as any, 'bold');
  doc.text('Invoice details', rightCol, margin + 10);
  doc.setFont(undefined as any, 'normal');
  let ry = margin + 15;
  doc.text(`Invoice #: ${invoice.invoiceNumber}`, rightCol, ry);
  ry += 5;
  doc.text(`Invoice date: ${formatDate(invoice.invoiceDate)}`, rightCol, ry);
  ry += 5;
  if (invoice.dueDate) {
    doc.text(`Due date: ${formatDate(invoice.dueDate)}`, rightCol, ry);
    ry += 5;
  }
  if (invoice.purchaseOrderId) {
    doc.text(`PO ref: ${invoice.purchaseOrderId}`, rightCol, ry);
    ry += 5;
  }
  doc.text(`Pricing: ${gstModeLabel(gstMode)}`, rightCol, ry);
  y = Math.max(y, ry) + 8;

  doc.setFont(undefined as any, 'bold');
  doc.text('Bill to', margin, y);
  doc.setFont(undefined as any, 'normal');
  y += 5;
  doc.text(businessInfo.businessName || '—', margin, y);
  y += 5;
  if (businessInfo.address) {
    doc.text(businessInfo.address, margin, y);
    y += 5;
  }
  if (businessInfo.gstNumber) {
    doc.text(`GST #${businessInfo.gstNumber}`, margin, y);
    y += 5;
  }
  y += 6;

  const tableBody = invoice.items.map((line) => {
    const lineTotal = line.quantity * line.unitPrice;
    const lineGst = gstMode === 'inclusive' ? lineTotal * (15 / 115) : lineTotal * 0.15;
    const lineEx = gstMode === 'inclusive' ? lineTotal * (100 / 115) : lineTotal;
    return [
      line.description,
      String(line.quantity),
      line.unit ?? 'units',
      `$${line.unitPrice.toFixed(2)}`,
      `$${lineEx.toFixed(2)}`,
      `$${lineGst.toFixed(2)}`,
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['Description', 'Qty', 'Unit', 'Unit price', 'Ex. GST', 'GST']],
    body: tableBody,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [55, 65, 81] },
    margin: { left: margin, right: margin },
  });

  y = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y + 40;
  y += 8;
  const totalsX = pageWidth - margin - 70;
  doc.setFontSize(10);
  doc.text('Subtotal (ex. GST):', totalsX, y);
  doc.text(`$${totals.subtotalExGst.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
  y += 6;
  doc.text('GST (15%):', totalsX, y);
  doc.text(`$${totals.gstAmount.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
  y += 6;
  doc.setFont(undefined as any, 'bold');
  doc.text('Total (inc. GST):', totalsX, y);
  doc.text(`$${totals.totalIncGst.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
  doc.setFont(undefined as any, 'normal');
  y += 10;

  if (invoice.paymentStatus) {
    doc.text(`Payment status: ${invoice.paymentStatus}`, margin, y);
    y += 6;
  }
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(
    'GST shown separately at 15%. Retain this document for GST records.',
    margin,
    y,
    { maxWidth: pageWidth - margin * 2 },
  );

  const invNum = invoice.invoiceNumber.replace(/[^\w-]/g, '_');
  doc.save(filename ?? `supplier-tax-invoice-${invNum}.pdf`);
}
